'use client';

import { PriceZoneSystem } from '@schuelerhilfe/shared/src/enums/priceZoneSystem';
import { derivedPrivacyPolicyPath } from '@schuelerhilfe/shared/src/seo/privacyPolicyPath';
import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { DEFAULT_WIDGET_SPACING, WIDGET_SPACING_CLASS } from '../spacing';
import { buildLocalBusinessJsonLd } from './LocalBusinessJsonLd';
import { collapseBlankLines, mapOfficeHours } from './schoolDetailsHelpers';
import type { SchoolDetailsResolved } from './types';
import { Icon } from '@/components/Icon';
import { JsonLdScript } from '@/components/JsonLdScript';
import { Modal } from '@/components/Modal';
import { Section } from '@/components/Section';
import { withAssetPrefix } from '@/lib/assetPrefix';
import { BRAND_PROFILES } from '@/lib/brandProfiles';
import { resolveCenterName } from '@/lib/location/centerName';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';
import { useRuntimeConfig } from '@/lib/runtime-config/RuntimeConfigProvider';
import { sanitizeRichHtml } from '@/lib/sanitize';

export function SchoolDetailsComponent({
  resolved,
}: {
  resolved: SchoolDetailsResolved;
}) {
  const { entities, imageUrl } = useRenderContext();
  const { brand, canonicalOrigin } = useRuntimeConfig();
  const t = useTranslations('schoolDetails');
  const location = entities.location;

  if (!location) {
    return null;
  }

  const system = brand === 'ien' ? PriceZoneSystem.ERNA : PriceZoneSystem.SH;
  const displayName = resolveCenterName(location);
  const jsonLd = buildLocalBusinessJsonLd(location, system, canonicalOrigin, {
    profile: BRAND_PROFILES[brand],
    reviews: resolved.reviews,
  });
  const officeHours = mapOfficeHours(location.officeHours, t);

  const principalCenter = location.personCenters?.find(
    (pc) => pc.mainLeader && !pc.disabled,
  );
  const principal = principalCenter?.person;

  return (
    // The header/subtitle pair this renders is a Text Content Widget in all
    // but name, so it carries the same editor spacing knob.
    <Section
      header={t('sectionHeader')}
      subtitle={displayName}
      // Mirrors the Strapi default, for entries written before the field
      // existed and for a failed resolve, which answers `{}`.
      className={
        WIDGET_SPACING_CLASS[resolved.spacing ?? DEFAULT_WIDGET_SPACING]
      }
    >
      {jsonLd && <JsonLdScript data={jsonLd} />}

      {/*
        The photo column exists only when there is a photo to put in it
        (SH-415). A principal record without one used to render a grey circle
        with a user icon; now the widget falls through to the single-column
        layout it already uses when there is no principal at all, so nothing
        stands in for the missing image. The principal's name and role still
        render — only the image column goes.
      */}
      {principal?.image ? (
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
          <div className="flex w-full justify-center lg:w-1/2">
            <Image
              src={imageUrl({
                ...principal.image,
                url: principal.image.url ?? undefined,
              })}
              // The partner's own alt text when they wrote one in the portal,
              // the principal's name otherwise. Not substituted: the portal
              // field takes no placeholders (same as Teachers and Rooms).
              alt={
                principal.image.altText?.trim() ||
                `${principal.firstname ?? ''} ${principal.lastname ?? ''}`.trim()
              }
              width={280}
              height={280}
              className="aspect-square h-56 w-56 rounded-full object-cover lg:h-72 lg:w-72"
            />
          </div>
          <div className="w-full lg:w-1/2">
            <InfoColumn
              location={location}
              principal={principal}
              officeHours={officeHours}
              resolved={resolved}
            />
          </div>
        </div>
      ) : (
        <div className="mx-auto w-full lg:w-1/2">
          <InfoColumn
            location={location}
            principal={principal}
            officeHours={officeHours}
            resolved={resolved}
          />
        </div>
      )}

      {/* SH-344 item 2.11. A franchise Standort's own "Einleitungstext" replaces
          the central SEO text here; a GmbH Standort keeps that text untouched,
          because `centerIntroduction` is written only through the
          Franchise-Partner-Portal.

          The fallback is deliberate: a franchise partner who has not filled the
          new field in yet keeps whatever their page shows today, so nothing
          disappears between this shipping and partners getting to it. That also
          means the two can never both render — this is one section with two
          possible sources, not two sections. */}
      {introductionText(location) && (
        <ExpandableText
          title={t('seoTitle', { cityName: resolveCenterName(location) })}
          text={introductionText(location)!}
        />
      )}

      <DirectionsSection location={location} />
    </Section>
  );
}

// --- Info Column ---

function InfoColumn({
  location,
  principal,
  officeHours,
  resolved,
}: {
  location: NonNullable<
    ReturnType<
      typeof import('@/lib/render/RenderContextProvider').useRenderContext
    >['entities']['location']
  >;
  principal:
    | {
        firstname?: string | null;
        lastname?: string | null;
        /** SH-344 item 3: the partner's own role line, when they wrote one. */
        title?: string | null;
      }
    | undefined;
  officeHours: { day: string; hours: string }[];
  resolved: SchoolDetailsResolved;
}) {
  const [isImpressumOpen, setIsImpressumOpen] = useState(false);
  const { entities, substitutePlaceholders } = useRenderContext();
  const t = useTranslations('schoolDetails');
  // Three tiers (10.03.02.01.13 § AC 3). The middle one is derived rather
  // than stored: one slug-less template Page backs every Location Page, so a
  // single CMS value cannot be right for GmbH *and* franchise Standorte — the
  // franchise variant has to point at the other URL family because a franchise
  // Standort is its own data controller.
  //
  // 1. the widget's own CMS field, when an editor set one and its placeholders
  //    resolve — the per-page escape hatch for a Standort needing a
  //    hand-written target;
  // 2. the Standort's own Datenschutzerklärung, derived from the shared path
  //    helper so the widget and the route cannot disagree about the URL shape;
  // 3. the domain-level `DomainSettings.privacyPolicyUrl`, which points at the
  //    generic per-brand page.
  const privacyPolicyUrl =
    substituteHref(resolved.privacyPolicyUrl, substitutePlaceholders) ||
    (derivedPrivacyPolicyPath(location) ?? entities.privacyPolicyUrl);

  return (
    <div className="flex flex-col">
      {principal && (
        <div>
          <p className="text-lg font-medium">
            {`${principal.firstname ?? ''} ${principal.lastname ?? ''}`.trim()}
          </p>
          {/* SH-344 item 3: the Franchise-Partner's own role line, when they
              wrote one. `title` exists on a stored person only because the
              portal put it there — SH's person payload has no field for it —
              so no franchise check is needed, and every other Standort keeps
              the standard "Schülerhilfe-Leitung in {cityName}" line. */}
          <p className="text-base">
            {principal.title?.trim()
              ? principal.title
              : t('principalRole', { cityName: location.cityName ?? '' })}
          </p>
        </div>
      )}
      <div
        className={classNames(
          'flex flex-col gap-8 sm:flex-row sm:items-start sm:gap-8',
          principal && 'pt-4',
        )}
      >
        {officeHours.length > 0 && (
          <div>
            <table className="text-sm">
              <tbody>
                {officeHours.map((entry) => (
                  <tr key={entry.day}>
                    <td className="py-1 pr-8 font-medium">{entry.day}</td>
                    <td className="py-1">{entry.hours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/*
              The Franchise-Partner's own note replaces the standard line when
              they wrote one (SH-344 item 4). Only a franchise Standort can have
              one — `officeHoursNote` lives on `LocationCustomization`, which no
              GmbH Standort carries — so no franchise check is needed here
              beyond the value itself being present.

              `text-xs` is 12px/16px, which is what the legacy page's
              `.location-operation-time .text-smaller` renders at; the styling
              is unchanged, only the source of the text.

              `whitespace-pre-line` because the partner types this in a
              multi-line field and their line breaks are the only structure it
              has. Not substituted: the field takes no placeholders, so what
              they type is what a visitor reads.
            */}
            <p className="mt-3 text-xs whitespace-pre-line">
              {location.officeHoursNote || t('openingHoursNote')}
            </p>
          </div>
        )}
        {location.isoCertified && (
          <Image
            src={withAssetPrefix('/img/tuev-nord-cert.svg')}
            alt={t('tuvAlt')}
            width={140}
            height={185}
            className="shrink-0 self-center object-contain sm:self-start"
          />
        )}
      </div>
      <div className="mt-4 flex gap-4 text-sm">
        <button
          type="button"
          onClick={() => setIsImpressumOpen(true)}
          className="text-primary hover:underline"
        >
          {t('impressum')}
        </button>
        <a href={privacyPolicyUrl} className="text-primary hover:underline">
          {t('privacyPolicy')}
        </a>
      </div>

      <Modal
        open={isImpressumOpen}
        onClose={() => setIsImpressumOpen(false)}
        titleId="impressum-modal-title"
      >
        {/* Not a heading: the closed modal is in the page HTML and would add
            one to every Standort page (70.15-Heading-Structure § 5). */}
        <p id="impressum-modal-title" className="h2 mb-4 text-xl font-medium">
          {t('impressum')}
        </p>
        <div className="space-y-4 text-sm">
          <ImpressumContent />
        </div>
      </Modal>
    </div>
  );
}

// --- Impressum Content (from RenderContext, backend-composed) ---

function ImpressumContent() {
  const { entities } = useRenderContext();
  const impressum = entities.impressum;

  // Two shapes reach this slot, and until SH-344 only one of them rendered.
  //
  // A GmbH Standort gets `DomainSettings.impressum`, which is Strapi rich text —
  // an array of blocks. A **franchise** Standort gets its own
  // `Locations.impressum`, which is a string column holding **HTML**: that is
  // what the SH Dashboard stores and what the legacy site published (389 of the
  // 390 Standorte with an imprint carry markup; the one that does not is plain
  // text, which this path renders just as well).
  //
  // The array check below used to drop the string case silently, so a franchise
  // partner's imprint was saved, synced — and rendered as nothing at all. It is
  // the one text such a Standort is legally required to show (SH-344 item 2.7).
  if (typeof impressum === 'string') {
    // Filtered to the same tag allow-list every other editor-authored HTML on
    // the site goes through. The markup reaches us from the SH Dashboard rather
    // than from our own CMS, so it is sanitised rather than trusted: the imprint
    // is public, unauthenticated page content, and nothing here needs a script,
    // an iframe or an inline style.
    const html = sanitizeRichHtml(impressum).trim();
    if (!html) {
      return null;
    }
    // `whitespace-pre-line` so the single plain-text imprint keeps its line
    // breaks; it is inert for the markup ones, whose `<p>`/`<br>` carry the
    // structure themselves.
    return (
      <div
        className="whitespace-pre-line"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  if (!Array.isArray(impressum) || impressum.length === 0) {
    return null;
  }

  return <RichText nodes={impressum} />;
}

/**
 * Which text the introduction section shows: the Franchise-Partner's own, else
 * the central SEO text (SH-344 item 2.11).
 *
 * `centerIntroduction` reaches the DTO only for a Standort that has one, and only
 * a franchise partner can write one — but the `franchise` check is explicit
 * anyway, because `dashboardData` is an SH-owned column and a GmbH Standort could
 * in principle arrive carrying one. The decision was "GmbH Standorte keep
 * `locationDescription` untouched", and this is what says so.
 */
function introductionText(location: {
  franchise?: boolean;
  centerIntroduction?: string;
  locationDescription?: string;
}): string | undefined {
  return (
    (location.franchise ? location.centerIntroduction : undefined) ??
    location.locationDescription
  );
}

// --- Expandable Text ---

function ExpandableText({ title, text }: { title: string; text: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const t = useTranslations('schoolDetails');

  return (
    <div>
      {/* An H2, as on the legacy Standort page (SH-505). */}
      <h2 className="md:text-2xl">{title}</h2>
      <div className="relative pt-3">
        {/* `whitespace-pre-line`, because a franchise partner types their
            Einleitungstext into a plain textarea and its line breaks are the
            only structure it has. `collapseBlankLines` keeps one empty line
            between paragraphs and throws the rest away — see the helper. */}
        <p
          className={classNames(
            'overflow-hidden text-sm leading-5.5 whitespace-pre-line transition-all duration-300 md:text-base',
            isExpanded ? 'max-h-250' : 'max-h-18',
          )}
        >
          {collapseBlankLines(text)}
        </p>
        {!isExpanded && (
          <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-10 bg-linear-to-t from-white to-transparent" />
        )}
      </div>
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        className="text-primary mt-2 text-sm font-medium hover:underline md:text-base"
      >
        {isExpanded ? t('collapse') : t('readMore')}
      </button>
    </div>
  );
}

// --- Directions Section ---

function DirectionsSection({
  location,
}: {
  location: NonNullable<
    ReturnType<
      typeof import('@/lib/render/RenderContextProvider').useRenderContext
    >['entities']['location']
  >;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const t = useTranslations('schoolDetails');

  const hasDirections =
    location.directions ||
    location.directionsTram ||
    location.parkingPossibilities;

  if (!hasDirections) {
    return null;
  }

  return (
    // `pt-6` is the gap the Expandable Text above used to carry as `mb-6`.
    <div className="pt-6">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="hover:text-primary mx-auto flex items-center gap-2 text-base font-medium"
      >
        {/* The button's own text names it; the pin is decoration. */}
        <svg
          width="24"
          height="24"
          viewBox="0 0 16 16"
          fill="none"
          className="shrink-0"
          aria-hidden="true"
        >
          <path
            d="M8 1C5.24 1 3 3.24 3 6c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5zm0 7a2 2 0 110-4 2 2 0 010 4z"
            fill="currentColor"
          />
        </svg>
        {t('directionsLabel', { cityName: location.cityName ?? '' })}
        <Icon
          src={
            isExpanded
              ? '/icons/svg/chevron-up.svg'
              : '/icons/svg/chevron-down.svg'
          }
          size={20}
          className="shrink-0"
        />
      </button>
      {isExpanded && (
        <p className="mt-4 text-base leading-relaxed whitespace-pre-line">
          {location.directions && (
            <>
              <span className="font-medium">{t('directionsHeading')}</span>
              <br />
              {location.directions}
              <br />
              <br />
            </>
          )}
          {location.directionsTram && (
            <>
              <span className="font-medium">{t('transitHeading')}</span>
              <br />
              {location.directionsTram}
              <br />
              <br />
            </>
          )}
          {location.parkingPossibilities && (
            <>
              <span className="font-medium">{t('parkingHeading')}</span>
              <br />
              {location.parkingPossibilities}
            </>
          )}
        </p>
      )}
    </div>
  );
}
